import { Fragment, useCallback, useMemo, useState } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { DataGrid, KeenIcon } from '@/components';
import { useNavigate } from 'react-router-dom';
import {
  useGetEmailLayoutsQuery,
  useUpdateEmailLayoutMutation,
} from '@/services/EmailLayoutService';
import { EmailLayout } from '@/types/email-layout.types';
import { PaginationState } from '@tanstack/react-table';

type RowCellProps = { row: { original: EmailLayout } };

const hasLayoutsArray = (raw: unknown): raw is { layouts: EmailLayout[] } => {
  return (
    !!raw &&
    typeof raw === 'object' &&
    !Array.isArray(raw) &&
    'layouts' in raw &&
    Array.isArray((raw as { layouts?: unknown }).layouts)
  );
};

const hasNumberField = <K extends string>(
  raw: unknown,
  key: K
): raw is Record<K, number> => {
  return (
    !!raw &&
    typeof raw === 'object' &&
    !Array.isArray(raw) &&
    key in raw &&
    typeof (raw as Record<K, unknown>)[key] === 'number'
  );
};

const NameCell = ({ layout }: { layout: EmailLayout }) => {
  return (
    <span className="font-medium text-gray-800">
      {layout.name || 'Default Layout'}
    </span>
  );
};

const DefaultCell = ({
  layout,
  onToggle,
  disabled,
}: {
  layout: EmailLayout;
  onToggle: (layout: EmailLayout) => void;
  disabled: boolean;
}) => {
  return (
    <label className="switch switch-sm" title="Set as default layout">
      <input
        type="checkbox"
        checked={!!layout.is_default}
        onChange={() => onToggle(layout)}
        disabled={disabled}
        className="cursor-pointer"
        aria-label="Toggle default layout"
      />
    </label>
  );
};

const ActionsCell = ({
  layout,
  onView,
}: {
  layout: EmailLayout;
  onView: (id: string) => void;
}) => {
  return (
    <div className="flex gap-2">
      <button
        className="btn btn-sm btn-icon btn-light"
        onClick={() => onView(layout.id)}
        title="View"
      >
        <KeenIcon icon="eye" />
      </button>
    </div>
  );
};

const renderNameCell = ({ row }: RowCellProps) => {
  return <NameCell layout={row.original} />;
};

const createRenderDefaultCell = (
  onToggle: (layout: EmailLayout) => void,
  updatingDefaultForId: string | null
) => {
  return ({ row }: RowCellProps) => {
    return (
      <DefaultCell
        layout={row.original}
        onToggle={onToggle}
        disabled={
          updatingDefaultForId === row.original.id || !!row.original.is_default
        }
      />
    );
  };
};

const createRenderActionsCell = (onView: (id: string) => void) => {
  return ({ row }: RowCellProps) => {
    return <ActionsCell layout={row.original} onView={onView} />;
  };
};

const EmailLayoutListing = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();

  const [updateLayout] = useUpdateEmailLayoutMutation();
  const [updatingDefaultForId, setUpdatingDefaultForId] = useState<
    string | null
  >(null);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const { data: layoutsData, isLoading } = useGetEmailLayoutsQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
  });

  const handleToggleDefault = useCallback(
    async (layout: EmailLayout) => {
      if (!layout?.id) return;
      try {
        setUpdatingDefaultForId(layout.id);
        await updateLayout({
          id: layout.id,
          body: { is_default: !layout.is_default },
        }).unwrap();
      } catch (e) {
        console.error(e);
      } finally {
        setUpdatingDefaultForId(null);
      }
    },
    [updateLayout]
  );

  const renderDefaultCell = useMemo(
    () => createRenderDefaultCell(handleToggleDefault, updatingDefaultForId),
    [handleToggleDefault, updatingDefaultForId]
  );

  const renderActionsCell = useMemo(
    () =>
      createRenderActionsCell((layoutId) =>
        navigate(`/template-layout-management/view/${layoutId}`)
      ),
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: renderNameCell,
      },
      {
        accessorKey: 'is_default',
        header: 'Is Default',
        cell: renderDefaultCell,
        meta: { className: 'w-32' },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: renderActionsCell,
        meta: { className: 'w-24' },
      },
    ],
    [renderDefaultCell, renderActionsCell]
  );

  // Extract layouts from response
  const layouts = useMemo(() => {
    const raw = layoutsData?.data;
    if (hasLayoutsArray(raw)) {
      return raw.layouts;
    } else if (Array.isArray(raw)) {
      return raw as EmailLayout[];
    }
    return [];
  }, [layoutsData]);

  // Extract pagination info
  const totalItems = useMemo(() => {
    const raw = layoutsData?.data;
    if (hasNumberField(raw, 'totalItems')) {
      return raw.totalItems || 0;
    }
    return layouts.length;
  }, [layoutsData, layouts.length]);

  const totalPages = useMemo(() => {
    const raw = layoutsData?.data;
    if (hasNumberField(raw, 'totalPages')) {
      return raw.totalPages || 0;
    }
    return Math.ceil(totalItems / pagination.pageSize);
  }, [layoutsData, totalItems, pagination.pageSize]);

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle
                id="template-layout-management-heading"
                text="Email Layouts"
              />
              <ToolbarDescription>
                Manage email template layouts
              </ToolbarDescription>
            </ToolbarHeading>
          </Toolbar>
        </Container>
      )}

      <div className="container-fixed">
        <div className="card">
          <div className="card-body">
            <DataGrid
              columns={columns}
              data={layouts}
              loading={isLoading}
              serverSide
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                totalItems,
                totalPages,
                setPagination,
              }}
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export { EmailLayoutListing };
